import { useState, useMemo } from 'react'
import { useQuery, useMutation } from '@tanstack/react-query'
import { 
  TrendingUp, TrendingDown, AlertTriangle,
  Download, Info, ChevronDown, ChevronUp, Loader2, Target,
  Wallet, Calculator, Calendar, FileText
} from 'lucide-react'
import {
  XAxis, YAxis, Tooltip, ResponsiveContainer,
  AreaChart, Area, CartesianGrid, Legend, ReferenceLine
} from 'recharts'
import api from '../services/api'
import { useWorkspace } from '../hooks/useWorkspace'
import { useLanguage } from '../hooks/useLanguage'

// ==================== TYPES ====================

interface MonthlyAggregate {
  month: string;
  label: string;
  income: number;
  expenses: number;
  fixed: number;
  variable: number;
  transaction_count: number;
  source_files: string[];
}

interface ForecastPrediction {
  month: string;
  label: string;
  predicted_expenses: number;
  predicted_income: number;
  predicted_fixed: number;
  predicted_variable: number;
  confidence: number;
  trend: 'up' | 'down' | 'stable';
  methodology: {
    base_value: number;
    trend_adjustment: number;
    seasonality_factor: number;
    volatility_penalty: number;
    formula: string;
  };
}

interface ForecastInsight {
  type: 'warning' | 'info' | 'success';
  code: string;
  title: string;
  message: string;
  data_points: number[];
  threshold: number;
}

interface ForecastStatistics {
  avg_monthly_expenses: number;
  avg_monthly_income: number;
  expense_trend: number;
  income_trend: number;
  volatility: number;
  data_points_count: number;
  date_range: {
    start: string;
    end: string;
  };
}

interface ForecastAudit {
  version: string;
  computed_at: string;
  workspace_id: string;
  input_summary: {
    total_transactions: number;
    date_range: { start: string; end: string };
    months_analyzed: number;
    source_files: string[];
  };
  calculations: Array<{
    step: string;
    formula: string;
    inputs: Record<string, number>;
    output: number;
  }>;
  data_quality: {
    completeness_score: number;
    missing_months: string[];
    data_gaps: Array<{ start: string; end: string }>;
  };
}

interface EnterpriseForecastData {
  historical: MonthlyAggregate[];
  forecasts: ForecastPrediction[];
  insights: ForecastInsight[];
  statistics: ForecastStatistics;
  audit: ForecastAudit;
  version: string;
  computed_at: string;
}

interface BusinessScenario {
  id: string;
  name: string;
  description: string;
  expenseAdjustment: number; // percentage -100 to +100
  incomeAdjustment: number;  // percentage -100 to +100
  oneTimeExpense: number;
  oneTimeIncome: number;
}

// ==================== COMPONENT ====================

export default function BusinessPlan() {
  const { currentWorkspace } = useWorkspace()
  const { language } = useLanguage()
  const [monthsToProject, setMonthsToProject] = useState(12)
  const [showAudit, setShowAudit] = useState(false)
  const [showMethodology, setShowMethodology] = useState(false)
  
  // Custom scenario
  const [scenario, setScenario] = useState<BusinessScenario>({
    id: 'custom',
    name: 'Scenario Personalizzato',
    description: 'Modifica i parametri per creare il tuo scenario',
    expenseAdjustment: 0,
    incomeAdjustment: 0,
    oneTimeExpense: 0,
    oneTimeIncome: 0,
  })

  // Preset scenarios
  const presetScenarios: BusinessScenario[] = [
    {
      id: 'conservative',
      name: language === 'it' ? 'Conservativo' : 'Conservative',
      description: language === 'it' ? 'Spese +10%, Entrate -5%' : 'Expenses +10%, Income -5%',
      expenseAdjustment: 10,
      incomeAdjustment: -5,
      oneTimeExpense: 0,
      oneTimeIncome: 0,
    },
    {
      id: 'optimistic',
      name: language === 'it' ? 'Ottimistico' : 'Optimistic',
      description: language === 'it' ? 'Spese -10%, Entrate +10%' : 'Expenses -10%, Income +10%',
      expenseAdjustment: -10,
      incomeAdjustment: 10,
      oneTimeExpense: 0,
      oneTimeIncome: 0,
    },
    {
      id: 'baseline',
      name: language === 'it' ? 'Baseline' : 'Baseline',
      description: language === 'it' ? 'Nessun aggiustamento' : 'No adjustments',
      expenseAdjustment: 0,
      incomeAdjustment: 0,
      oneTimeExpense: 0,
      oneTimeIncome: 0,
    },
  ]

  // Fetch enterprise forecast data
  const { data: forecastData, isLoading, error } = useQuery<EnterpriseForecastData>({
    queryKey: ['enterprise-forecast', currentWorkspace?.id, monthsToProject],
    queryFn: async () => {
      const now = new Date()
      const startDate = new Date(now.getFullYear() - 1, now.getMonth(), 1)
      const response = await api.get(
        `/forecasts/enterprise?months=${monthsToProject}&start_date=${startDate.toISOString().split('T')[0]}&end_date=${now.toISOString().split('T')[0]}`
      )
      return response.data
    },
    enabled: !!currentWorkspace,
  })

  // PDF Export mutation
  const pdfExportMutation = useMutation({
    mutationFn: async () => {
      if (!forecastData || !projections.length) throw new Error('Dati non disponibili')
      
      // Build BusinessPlanJSON structure for PDF generation
      const businessPlanJSON = {
        version: '1.0.0',
        generated_at: new Date().toISOString(),
        scenario: {
          name: scenario.name,
          description: scenario.description,
          expenseAdjustment: 1 + scenario.expenseAdjustment / 100,
          incomeAdjustment: 1 + scenario.incomeAdjustment / 100,
        },
        historical: {
          period: {
            start: forecastData.statistics.date_range.start,
            end: forecastData.statistics.date_range.end,
          },
          totalTransactions: forecastData.audit.input_summary.total_transactions,
          monthsCovered: forecastData.audit.input_summary.months_analyzed,
          averageMonthlyExpenses: forecastData.statistics.avg_monthly_expenses,
          averageMonthlyIncome: forecastData.statistics.avg_monthly_income,
          expensesTrend: forecastData.statistics.expense_trend,
          incomeTrend: forecastData.statistics.income_trend,
          expensesVolatility: forecastData.statistics.volatility,
          incomeVolatility: forecastData.statistics.volatility * 0.8, // Approximation
        },
        projections: projections.map(p => ({
          month: p.month,
          projectedExpenses: p.adjustedExpenses,
          projectedIncome: p.adjustedIncome,
          netCashFlow: p.netCashFlow,
          confidenceLevel: p.confidence,
          components: {
            baseValue: p.methodology.base_value,
            trend: p.methodology.trend_adjustment,
            seasonality: p.methodology.seasonality_factor,
            volatilityPenalty: p.methodology.volatility_penalty,
          },
        })),
        statistics: {
          totalProjectedExpenses: projections.reduce((s, p) => s + p.adjustedExpenses, 0),
          totalProjectedIncome: projections.reduce((s, p) => s + p.adjustedIncome, 0),
          totalNetCashFlow: projections.reduce((s, p) => s + p.netCashFlow, 0),
          averageMonthlyExpenses: projections.reduce((s, p) => s + p.adjustedExpenses, 0) / projections.length,
          averageMonthlyIncome: projections.reduce((s, p) => s + p.adjustedIncome, 0) / projections.length,
          averageConfidence: projections.reduce((s, p) => s + p.confidence, 0) / projections.length,
        },
        audit: {
          generated_at: new Date().toISOString(),
          data_source: 'FinDesk - Transazioni Contabili',
          methodology_version: forecastData.version || '2.0.0',
          workspace_id: currentWorkspace?.id || 'N/A',
          workspace_name: currentWorkspace?.name || 'Cliente',
          generator_version: '1.0.0',
        },
      }
      
      // Call API to generate HTML
      const response = await api.post('/business-plan/export', { businessPlan: businessPlanJSON }, {
        responseType: 'text',
      })
      
      return response.data
    },
    onSuccess: (html) => {
      // Open HTML in new window for print/PDF
      const printWindow = window.open('', '_blank')
      if (printWindow) {
        printWindow.document.write(html)
        printWindow.document.close()
        // Auto-trigger print dialog for PDF save
        setTimeout(() => {
          printWindow.print()
        }, 500)
      }
    },
  })

  // Calculate projections based on scenario
  const projections = useMemo(() => {
    if (!forecastData?.forecasts) return []

    return forecastData.forecasts.map((f) => {
      const adjustedExpenses = f.predicted_expenses * (1 + scenario.expenseAdjustment / 100)
      const adjustedIncome = f.predicted_income * (1 + scenario.incomeAdjustment / 100)
      const netCashFlow = adjustedIncome - adjustedExpenses

      return {
        ...f,
        adjustedExpenses: Math.round(adjustedExpenses * 100) / 100,
        adjustedIncome: Math.round(adjustedIncome * 100) / 100,
        netCashFlow: Math.round(netCashFlow * 100) / 100,
        baselineNetCashFlow: Math.round((f.predicted_income - f.predicted_expenses) * 100) / 100,
      }
    })
  }, [forecastData, scenario])

  // Calculate summary metrics
  const summaryMetrics = useMemo(() => {
    if (!forecastData || !projections.length) return null

    const stats = forecastData.statistics
    const totalProjectedExpenses = projections.reduce((sum, p) => sum + p.adjustedExpenses, 0)
    const totalProjectedIncome = projections.reduce((sum, p) => sum + p.adjustedIncome, 0)
    const totalNetCashFlow = totalProjectedIncome - totalProjectedExpenses + scenario.oneTimeIncome - scenario.oneTimeExpense
    
    // Runway calculation (months until cash runs out at current burn rate)
    const avgMonthlyBurn = totalProjectedExpenses / projections.length
    const avgMonthlyIncome = totalProjectedIncome / projections.length
    const monthlyNetBurn = avgMonthlyBurn - avgMonthlyIncome
    
    return {
      avgMonthlyExpenses: stats.avg_monthly_expenses,
      avgMonthlyIncome: stats.avg_monthly_income,
      projectedExpenses: totalProjectedExpenses,
      projectedIncome: totalProjectedIncome,
      projectedNetCashFlow: totalNetCashFlow,
      savingsRate: avgMonthlyIncome > 0 ? ((avgMonthlyIncome - avgMonthlyBurn) / avgMonthlyIncome) * 100 : 0,
      monthlyNetBurn,
      expenseTrend: stats.expense_trend,
      incomeTrend: stats.income_trend,
      dataQuality: forecastData.audit.data_quality.completeness_score * 100,
    }
  }, [forecastData, projections, scenario])

  const formatCurrency = (value: number) =>
    new Intl.NumberFormat(language === 'it' ? 'it-IT' : 'en-US', { 
      style: 'currency', 
      currency: 'EUR',
      maximumFractionDigits: 0,
    }).format(value)

  const formatPercent = (value: number) =>
    `${value >= 0 ? '+' : ''}${value.toFixed(1)}%`

  // Chart data
  const chartData = useMemo(() => {
    if (!forecastData) return []

    const historical = forecastData.historical.map(h => ({
      month: h.label,
      income: h.income,
      expenses: h.expenses,
      netCashFlow: h.income - h.expenses,
      type: 'historical',
    }))

    const projected = projections.map(p => ({
      month: p.label,
      income: p.adjustedIncome,
      expenses: p.adjustedExpenses,
      netCashFlow: p.netCashFlow,
      type: 'projected',
    }))

    return [...historical, ...projected]
  }, [forecastData, projections])

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        <span className="ml-3 text-gray-600 dark:text-gray-400">
          {language === 'it' ? 'Calcolo proiezioni...' : 'Computing projections...'}
        </span>
      </div>
    )
  }

  // Error state
  if (error) {
    return (
      <div className="p-6 bg-red-50 dark:bg-red-900/20 rounded-xl">
        <div className="flex items-center gap-3">
          <AlertTriangle className="w-6 h-6 text-red-500" />
          <h3 className="font-medium text-red-800 dark:text-red-200">
            {language === 'it' ? 'Errore nel caricamento' : 'Loading error'}
          </h3>
        </div>
        <p className="mt-2 text-sm text-red-600 dark:text-red-300">
          {(error as Error).message}
        </p>
      </div>
    )
  }

  // Insufficient data
  if (!forecastData || forecastData.historical.length < 2) {
    return (
      <div className="p-6 bg-yellow-50 dark:bg-yellow-900/20 rounded-xl">
        <div className="flex items-center gap-3">
          <AlertTriangle className="w-6 h-6 text-yellow-500" />
          <h3 className="font-medium text-yellow-800 dark:text-yellow-200">
            {language === 'it' ? 'Dati insufficienti' : 'Insufficient data'}
          </h3>
        </div>
        <p className="mt-2 text-sm text-yellow-600 dark:text-yellow-300">
          {language === 'it' 
            ? 'Carica almeno 2 mesi di estratti conto per generare un business plan.' 
            : 'Upload at least 2 months of bank statements to generate a business plan.'}
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-6 pt-1">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-lg font-semibold text-gray-900 dark:text-white">
            Business Plan
          </h1>
          <p className="text-gray-500 dark:text-gray-400 text-sm mt-0.5">
            {language === 'it' 
              ? 'Proiezioni finanziarie e scenari what-if basati sui tuoi dati storici'
              : 'Financial projections and what-if scenarios based on your historical data'}
          </p>
        </div>

        {/* Projection months selector */}
        <div className="flex items-center gap-2">
          <label className="text-sm text-gray-600 dark:text-gray-400">
            {language === 'it' ? 'Proietta per:' : 'Project for:'}
          </label>
          <select
            value={monthsToProject}
            onChange={(e) => setMonthsToProject(parseInt(e.target.value))}
            className="px-3 py-1.5 rounded-lg border border-gray-200 dark:border-slate-700 bg-white dark:bg-gray-800 text-sm"
          >
            <option value={3}>3 {language === 'it' ? 'mesi' : 'months'}</option>
            <option value={6}>6 {language === 'it' ? 'mesi' : 'months'}</option>
            <option value={12}>12 {language === 'it' ? 'mesi' : 'months'}</option>
            <option value={24}>24 {language === 'it' ? 'mesi' : 'months'}</option>
          </select>
        </div>
      </div>

      {/* Data Quality Banner */}
      {forecastData.audit.data_quality.completeness_score < 0.8 && (
        <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-xl border border-yellow-200 dark:border-yellow-800">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="font-medium text-yellow-800 dark:text-yellow-200">
                {language === 'it' ? 'Qualità dati migliorabile' : 'Data quality can be improved'}
              </h4>
              <p className="text-sm text-yellow-600 dark:text-yellow-300 mt-1">
                {language === 'it' 
                  ? `Completezza dati: ${(forecastData.audit.data_quality.completeness_score * 100).toFixed(0)}%. Carica più estratti conto per previsioni più accurate.`
                  : `Data completeness: ${(forecastData.audit.data_quality.completeness_score * 100).toFixed(0)}%. Upload more statements for more accurate forecasts.`}
              </p>
              {forecastData.audit.data_quality.missing_months.length > 0 && (
                <p className="text-xs text-yellow-600 dark:text-yellow-400 mt-1">
                  {language === 'it' ? 'Mesi mancanti: ' : 'Missing months: '}
                  {forecastData.audit.data_quality.missing_months.slice(0, 3).join(', ')}
                  {forecastData.audit.data_quality.missing_months.length > 3 && '...'}
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Summary Cards */}
      {summaryMetrics && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Projected Net Cash Flow */}
          <div className="p-5 bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-slate-700">
            <div className="flex items-center gap-3 mb-3">
              <div className={`p-2 rounded-lg ${summaryMetrics.projectedNetCashFlow >= 0 ? 'bg-green-100 dark:bg-green-900/30' : 'bg-red-100 dark:bg-red-900/30'}`}>
                <Target className={`w-5 h-5 ${summaryMetrics.projectedNetCashFlow >= 0 ? 'text-green-600' : 'text-red-600'}`} />
              </div>
              <span className="text-xs font-medium uppercase tracking-[0.08em] text-slate-400">
                {language === 'it' ? 'Cash Flow Netto' : 'Net Cash Flow'}
              </span>
            </div>
            <p className={`kpi-value kpi-animate ${summaryMetrics.projectedNetCashFlow >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              {formatCurrency(summaryMetrics.projectedNetCashFlow)}
            </p>
            <p className="text-xs text-gray-500 mt-1">
              {language === 'it' ? `Prossimi ${monthsToProject} mesi` : `Next ${monthsToProject} months`}
            </p>
          </div>

          {/* Projected Income */}
          <div className="p-5 bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-slate-700">
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2 rounded-lg bg-blue-100 dark:bg-blue-900/30">
                <TrendingUp className="w-5 h-5 text-blue-600" />
              </div>
              <span className="text-xs font-medium uppercase tracking-[0.08em] text-slate-400">
                {language === 'it' ? 'Entrate Proiettate' : 'Projected Income'}
              </span>
            </div>
            <p className="kpi-value kpi-animate text-gray-900 dark:text-white">
              {formatCurrency(summaryMetrics.projectedIncome)}
            </p>
            <p className="text-xs text-gray-500 mt-1">
              {language === 'it' ? 'Trend: ' : 'Trend: '}
              <span className={summaryMetrics.incomeTrend >= 0 ? 'text-green-600' : 'text-red-600'}>
                {formatPercent(summaryMetrics.incomeTrend)}
              </span>
            </p>
          </div>

          {/* Projected Expenses */}
          <div className="p-5 bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-slate-700">
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2 rounded-lg bg-orange-100 dark:bg-orange-900/30">
                <TrendingDown className="w-5 h-5 text-orange-600" />
              </div>
              <span className="text-xs font-medium uppercase tracking-[0.08em] text-slate-400">
                {language === 'it' ? 'Spese Proiettate' : 'Projected Expenses'}
              </span>
            </div>
            <p className="kpi-value kpi-animate text-gray-900 dark:text-white">
              {formatCurrency(summaryMetrics.projectedExpenses)}
            </p>
            <p className="text-xs text-gray-500 mt-1">
              {language === 'it' ? 'Trend: ' : 'Trend: '}
              <span className={summaryMetrics.expenseTrend <= 0 ? 'text-green-600' : 'text-red-600'}>
                {formatPercent(summaryMetrics.expenseTrend)}
              </span>
            </p>
          </div>

          {/* Savings Rate */}
          <div className="p-5 bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-slate-700">
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2 rounded-lg bg-purple-100 dark:bg-purple-900/30">
                <Wallet className="w-5 h-5 text-purple-600" />
              </div>
              <span className="text-xs font-medium uppercase tracking-[0.08em] text-slate-400">
                {language === 'it' ? 'Tasso Risparmio' : 'Savings Rate'}
              </span>
            </div>
            <p className="kpi-value kpi-animate text-gray-900 dark:text-white">
              {summaryMetrics.savingsRate.toFixed(1)}%
            </p>
            <p className="text-xs text-gray-500 mt-1">
              {language === 'it' ? 'Media mensile' : 'Monthly average'}
            </p>
          </div>
        </div>
      )}

      {/* Scenario Builder */}
      <div className="p-6 bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-slate-700">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-2">
            <Calculator className="w-5 h-5 text-blue-600" />
            {language === 'it' ? 'Costruttore Scenari' : 'Scenario Builder'}
          </h2>
          <div className="flex gap-2">
            {presetScenarios.map((ps) => (
              <button
                key={ps.id}
                onClick={() => setScenario(ps)}
                className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
                  scenario.id === ps.id
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
              >
                {ps.name}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {/* Expense Adjustment */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {language === 'it' ? 'Aggiustamento Spese' : 'Expense Adjustment'}
            </label>
            <div className="flex items-center gap-3">
              <input
                type="range"
                min="-50"
                max="50"
                value={scenario.expenseAdjustment}
                onChange={(e) => setScenario({ ...scenario, id: 'custom', expenseAdjustment: parseInt(e.target.value) })}
                className="flex-1"
              />
              <span className={`text-sm font-medium w-14 text-right ${scenario.expenseAdjustment > 0 ? 'text-red-600' : scenario.expenseAdjustment < 0 ? 'text-green-600' : 'text-gray-600'}`}>
                {formatPercent(scenario.expenseAdjustment)}
              </span>
            </div>
          </div>

          {/* Income Adjustment */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {language === 'it' ? 'Aggiustamento Entrate' : 'Income Adjustment'}
            </label>
            <div className="flex items-center gap-3">
              <input
                type="range"
                min="-50"
                max="50"
                value={scenario.incomeAdjustment}
                onChange={(e) => setScenario({ ...scenario, id: 'custom', incomeAdjustment: parseInt(e.target.value) })}
                className="flex-1"
              />
              <span className={`text-sm font-medium w-14 text-right ${scenario.incomeAdjustment < 0 ? 'text-red-600' : scenario.incomeAdjustment > 0 ? 'text-green-600' : 'text-gray-600'}`}>
                {formatPercent(scenario.incomeAdjustment)}
              </span>
            </div>
          </div>

          {/* One-time Expense */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {language === 'it' ? 'Spesa Una Tantum' : 'One-time Expense'}
            </label>
            <input
              type="number"
              value={scenario.oneTimeExpense}
              onChange={(e) => setScenario({ ...scenario, id: 'custom', oneTimeExpense: parseFloat(e.target.value) || 0 })}
              className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-slate-700 bg-white dark:bg-gray-900 text-sm"
              placeholder="€0"
            />
          </div>

          {/* One-time Income */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {language === 'it' ? 'Entrata Una Tantum' : 'One-time Income'}
            </label>
            <input
              type="number"
              value={scenario.oneTimeIncome}
              onChange={(e) => setScenario({ ...scenario, id: 'custom', oneTimeIncome: parseFloat(e.target.value) || 0 })}
              className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-slate-700 bg-white dark:bg-gray-900 text-sm"
              placeholder="€0"
            />
          </div>
        </div>
      </div>

      {/* Projection Chart */}
      <div className="p-6 bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-slate-700">
        <h2 className="text-sm font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
          <Calendar className="w-5 h-5 text-blue-600" />
          {language === 'it' ? 'Proiezione Temporale' : 'Timeline Projection'}
        </h2>

        <div className="h-80">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis 
                dataKey="month" 
                tick={{ fontSize: 12 }}
                stroke="#9ca3af"
              />
              <YAxis 
                tickFormatter={(v) => `€${(v/1000).toFixed(0)}k`}
                tick={{ fontSize: 12 }}
                stroke="#9ca3af"
              />
              <Tooltip 
                formatter={(value: number) => formatCurrency(value)}
                contentStyle={{
                  backgroundColor: 'rgba(255, 255, 255, 0.95)',
                  borderRadius: '8px',
                  border: '1px solid #e5e7eb',
                }}
              />
              <Legend />
              <ReferenceLine y={0} stroke="#000" strokeDasharray="3 3" />
              <Area 
                type="monotone" 
                dataKey="income" 
                name={language === 'it' ? 'Entrate' : 'Income'}
                stroke="#10b981"
                fill="#10b98140"
              />
              <Area 
                type="monotone" 
                dataKey="expenses" 
                name={language === 'it' ? 'Spese' : 'Expenses'}
                stroke="#f59e0b"
                fill="#f59e0b40"
              />
              <Area 
                type="monotone" 
                dataKey="netCashFlow" 
                name={language === 'it' ? 'Cash Flow Netto' : 'Net Cash Flow'}
                stroke="#3b82f6"
                fill="#3b82f640"
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        <div className="mt-4 flex items-center gap-6 text-sm text-gray-500">
          <div className="flex items-center gap-2">
            <div className="w-4 h-0.5 bg-gray-400"></div>
            <span>{language === 'it' ? 'Dati storici' : 'Historical data'}</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-0.5 bg-gray-400" style={{ borderStyle: 'dashed' }}></div>
            <span>{language === 'it' ? 'Proiezioni' : 'Projections'}</span>
          </div>
        </div>
      </div>

      {/* Monthly Breakdown */}
      <div className="p-6 bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-slate-700">
        <h2 className="text-sm font-semibold text-gray-900 dark:text-white mb-4">
          {language === 'it' ? 'Dettaglio Mensile' : 'Monthly Breakdown'}
        </h2>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 dark:border-slate-700">
                <th className="text-left py-3 px-4 text-xs font-medium text-slate-400 dark:text-slate-500 uppercase tracking-wider">
                  {language === 'it' ? 'Mese' : 'Month'}
                </th>
                <th className="text-right py-3 px-4 text-xs font-medium text-slate-400 dark:text-slate-500 uppercase tracking-wider">
                  {language === 'it' ? 'Entrate' : 'Income'}
                </th>
                <th className="text-right py-3 px-4 text-xs font-medium text-slate-400 dark:text-slate-500 uppercase tracking-wider">
                  {language === 'it' ? 'Spese' : 'Expenses'}
                </th>
                <th className="text-right py-3 px-4 text-xs font-medium text-slate-400 dark:text-slate-500 uppercase tracking-wider">
                  {language === 'it' ? 'Cash Flow' : 'Cash Flow'}
                </th>
                <th className="text-center py-3 px-4 text-xs font-medium text-slate-400 dark:text-slate-500 uppercase tracking-wider">
                  {language === 'it' ? 'Confidenza' : 'Confidence'}
                </th>
              </tr>
            </thead>
            <tbody>
              {projections.map((p) => (
                <tr key={p.month} className="border-b border-gray-100 dark:border-slate-700/50 hover:bg-black/[0.02] dark:hover:bg-white/[0.02]">
                  <td className="py-3 px-4 font-medium text-gray-900 dark:text-white">
                    {p.label}
                  </td>
                  <td className="py-3 px-4 text-right text-green-600">
                    {formatCurrency(p.adjustedIncome)}
                  </td>
                  <td className="py-3 px-4 text-right text-orange-600">
                    {formatCurrency(p.adjustedExpenses)}
                  </td>
                  <td className={`py-3 px-4 text-right font-medium ${p.netCashFlow >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {formatCurrency(p.netCashFlow)}
                  </td>
                  <td className="py-3 px-4 text-center">
                    <div className="flex items-center justify-center gap-2">
                      <div className="w-16 h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                        <div 
                          className={`h-full rounded-full ${p.confidence >= 70 ? 'bg-green-500' : p.confidence >= 50 ? 'bg-yellow-500' : 'bg-red-500'}`}
                          style={{ width: `${p.confidence}%` }}
                        />
                      </div>
                      <span className="text-xs text-gray-500">{p.confidence}%</span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Audit & Methodology Section */}
      <div className="p-6 bg-gray-50 dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-slate-700">
        <button
          onClick={() => setShowAudit(!showAudit)}
          className="w-full flex items-center justify-between text-left"
        >
          <div className="flex items-center gap-3">
            <Info className="w-5 h-5 text-blue-600" />
            <span className="font-medium text-gray-900 dark:text-white">
              {language === 'it' ? 'Audit Trail & Metodologia' : 'Audit Trail & Methodology'}
            </span>
          </div>
          {showAudit ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
        </button>

        {showAudit && forecastData && (
          <div className="mt-6 space-y-6">
            {/* Version & Timestamp */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-white dark:bg-gray-800 rounded-lg">
              <div>
                <span className="text-xs text-gray-500 uppercase tracking-wide">Versione</span>
                <p className="font-mono text-sm">{forecastData.version}</p>
              </div>
              <div>
                <span className="text-xs text-gray-500 uppercase tracking-wide">Calcolato</span>
                <p className="font-mono text-sm">{new Date(forecastData.computed_at).toLocaleString()}</p>
              </div>
              <div>
                <span className="text-xs text-gray-500 uppercase tracking-wide">Transazioni</span>
                <p className="font-mono text-sm">{forecastData.audit.input_summary.total_transactions}</p>
              </div>
              <div>
                <span className="text-xs text-gray-500 uppercase tracking-wide">Completezza</span>
                <p className="font-mono text-sm">{(forecastData.audit.data_quality.completeness_score * 100).toFixed(0)}%</p>
              </div>
            </div>

            {/* Methodology Toggle */}
            <button
              onClick={() => setShowMethodology(!showMethodology)}
              className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700"
            >
              {showMethodology ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              {language === 'it' ? 'Mostra dettagli calcoli' : 'Show calculation details'}
            </button>

            {showMethodology && (
              <div className="space-y-4">
                <h4 className="font-medium text-gray-900 dark:text-white">
                  {language === 'it' ? 'Dettaglio Calcoli' : 'Calculation Details'}
                </h4>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs font-mono">
                    <thead>
                      <tr className="border-b border-gray-200 dark:border-slate-700">
                        <th className="text-left py-2 px-3 text-xs font-medium text-slate-400 dark:text-slate-500 uppercase tracking-wider">Step</th>
                        <th className="text-left py-2 px-3 text-xs font-medium text-slate-400 dark:text-slate-500 uppercase tracking-wider">Formula</th>
                        <th className="text-left py-2 px-3 text-xs font-medium text-slate-400 dark:text-slate-500 uppercase tracking-wider">Input</th>
                        <th className="text-right py-2 px-3 text-xs font-medium text-slate-400 dark:text-slate-500 uppercase tracking-wider">Output</th>
                      </tr>
                    </thead>
                    <tbody>
                      {forecastData.audit.calculations.slice(0, 10).map((calc, i) => (
                        <tr key={i} className="border-b border-gray-100 dark:border-slate-700/50">
                          <td className="py-2 px-3 text-gray-900 dark:text-white">{calc.step}</td>
                          <td className="py-2 px-3 text-gray-600 dark:text-gray-400">{calc.formula}</td>
                          <td className="py-2 px-3 text-gray-600 dark:text-gray-400">
                            {Object.entries(calc.inputs).map(([k, v]) => `${k}=${v}`).join(', ')}
                          </td>
                          <td className="py-2 px-3 text-right text-blue-600">{calc.output}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {forecastData.audit.calculations.length > 10 && (
                  <p className="text-xs text-gray-500">
                    {language === 'it' ? `... e altri ${forecastData.audit.calculations.length - 10} calcoli` : `... and ${forecastData.audit.calculations.length - 10} more calculations`}
                  </p>
                )}
              </div>
            )}

            {/* Source Files */}
            <div>
              <h4 className="font-medium text-gray-900 dark:text-white mb-2">
                {language === 'it' ? 'File Sorgente' : 'Source Files'}
              </h4>
              <div className="flex flex-wrap gap-2">
                {forecastData.audit.input_summary.source_files.map((file, i) => (
                  <span key={i} className="px-2 py-1 text-xs bg-gray-100 dark:bg-gray-700 rounded">
                    {file}
                  </span>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Export Buttons */}
      <div className="flex justify-end gap-3">
        {/* Export PDF (Bancabile) */}
        <button
          onClick={() => pdfExportMutation.mutate()}
          disabled={pdfExportMutation.isPending || !forecastData}
          className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {pdfExportMutation.isPending ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <FileText className="w-4 h-4" />
          )}
          {language === 'it' ? 'Esporta PDF Bancabile' : 'Export Bank-Ready PDF'}
        </button>
        
        {/* Export JSON */}
        <button
          onClick={() => {
            // Export logic here
            const exportData = {
              generated_at: new Date().toISOString(),
              scenario,
              projections,
              statistics: forecastData?.statistics,
              audit: forecastData?.audit,
            }
            const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' })
            const url = URL.createObjectURL(blob)
            const a = document.createElement('a')
            a.href = url
            a.download = `business-plan-${new Date().toISOString().split('T')[0]}.json`
            a.click()
          }}
          className="flex items-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
        >
          <Download className="w-4 h-4" />
          {language === 'it' ? 'Esporta JSON' : 'Export JSON'}
        </button>
      </div>
    </div>
  )
}
